import type { AuctionSeed } from "../services/types";

/**
 * Auction seeds (Phase 3). Each seed references a group + cycle and, for closed
 * cycles, carries the complete bid history and the authoritative result. Pool,
 * installment and member count are resolved from the group at runtime so they
 * always agree with Phase 2. All closed cycles below divide their discount
 * evenly, so collection exactly equals payout (remainder 0). Real auction
 * figures come from the backend; these are illustrative demo records.
 */
export const MOCK_AUCTION_SEEDS: AuctionSeed[] = [
  // ----- Pending normal auctions -----
  // g1: 14 members, 6 cycles won → 8 eligible. Due today.
  { id: "au-g1c7", groupId: "g1", cycle: 7, scheduledDate: "2026-08-25T00:00:00.000Z" },
  // g2: 18 members, 4 cycles won → 14 eligible. Due today.
  { id: "au-g2c5", groupId: "g2", cycle: 5, scheduledDate: "2026-08-25T00:00:00.000Z" },
  // g3: 10 members, 8 cycles won → only 2 eligible. Overdue.
  { id: "au-g3c9", groupId: "g3", cycle: 9, scheduledDate: "2026-08-20T00:00:00.000Z" },
  // g4: 13 members, 2 cycles won → 11 eligible. Overdue.
  { id: "au-g4c3", groupId: "g4", cycle: 3, scheduledDate: "2026-08-22T00:00:00.000Z" },
  // g5: 9 members, 8 cycles won → final cycle, 1 eligible. Upcoming.
  { id: "au-g5c9", groupId: "g5", cycle: 9, scheduledDate: "2026-08-28T00:00:00.000Z" },
  // g6: 13 members, 10 cycles won → 3 eligible. Upcoming.
  { id: "au-g6c11", groupId: "g6", cycle: 11, scheduledDate: "2026-08-29T00:00:00.000Z" },
  // g7: 12 members, 3 cycles won → 9 eligible. Upcoming.
  { id: "au-g7c4", groupId: "g7", cycle: 4, scheduledDate: "2026-08-30T00:00:00.000Z" },

  // ----- Pending reserved organizer cycle -----
  // g14: cycle 2 is the organizer's reserved cycle — no auction, full pool.
  { id: "au-g14c2", groupId: "g14", cycle: 2, scheduledDate: "2026-08-25T00:00:00.000Z" },

  // ----- Closed cycles with full bid histories (collection == payout) -----
  {
    id: "au-g3c5",
    groupId: "g3",
    cycle: 5,
    scheduledDate: "2026-06-20T00:00:00.000Z",
    status: "closed",
    closedAt: "2026-06-20T11:00:00.000Z",
    closedBy: "Anita Desai",
    bids: [
      { id: "b-31", memberId: "m2", amount: 197000, placedAt: "2026-06-20T10:01:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-32", memberId: "m9", amount: 196000, placedAt: "2026-06-20T10:03:00.000Z", placedBy: "Anita Desai", status: "withdrawn" },
      { id: "b-33", memberId: "m17", amount: 195000, placedAt: "2026-06-20T10:05:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-34", memberId: "m32", amount: 193000, placedAt: "2026-06-20T10:08:00.000Z", placedBy: "Anita Desai", status: "withdrawn" },
      { id: "b-35", memberId: "m13", amount: 192000, placedAt: "2026-06-20T10:11:00.000Z", placedBy: "Anita Desai", status: "withdrawn" },
      { id: "b-36", memberId: "m22", amount: 191000, placedAt: "2026-06-20T10:14:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-37", memberId: "m32", amount: 188000, placedAt: "2026-06-20T10:18:00.000Z", placedBy: "Anita Desai", status: "standing" },
    ],
    result: {
      winnerMemberId: "m32",
      winningBid: 188000,
      discount: 12000,
      perMemberDividend: 1200,
      dividendRemainder: 0,
      netInstallment: 18800,
      totalCollected: 188000,
      winnerPendingDues: 0,
      winnerPayout: 188000,
      closedAt: "2026-06-20T11:00:00.000Z",
      closedBy: "Anita Desai",
    },
  },
  {
    id: "au-g1c5",
    groupId: "g1",
    cycle: 5,
    scheduledDate: "2026-06-10T00:00:00.000Z",
    status: "closed",
    closedAt: "2026-06-10T11:30:00.000Z",
    closedBy: "Anita Desai",
    bids: [
      { id: "b-11", memberId: "m1", amount: 139000, placedAt: "2026-06-10T10:02:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-12", memberId: "m18", amount: 137000, placedAt: "2026-06-10T10:06:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-13", memberId: "m15", amount: 136000, placedAt: "2026-06-10T10:09:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-14", memberId: "m8", amount: 134000, placedAt: "2026-06-10T10:12:00.000Z", placedBy: "Anita Desai", status: "standing" },
      { id: "b-15", memberId: "m8", amount: 133000, placedAt: "2026-06-10T10:16:00.000Z", placedBy: "Anita Desai", status: "standing" },
    ],
    result: {
      winnerMemberId: "m8",
      winningBid: 133000,
      discount: 7000,
      perMemberDividend: 500,
      dividendRemainder: 0,
      netInstallment: 9500,
      totalCollected: 133000,
      winnerPendingDues: 0,
      winnerPayout: 133000,
      closedAt: "2026-06-10T11:30:00.000Z",
      closedBy: "Anita Desai",
    },
  },
  {
    id: "au-g3c1",
    groupId: "g3",
    cycle: 1,
    scheduledDate: "2025-12-20T00:00:00.000Z",
    status: "closed",
    closedAt: "2025-12-20T10:45:00.000Z",
    closedBy: "Suresh Kumar",
    bids: [
      { id: "b-01", memberId: "m43", amount: 199000, placedAt: "2025-12-20T10:00:00.000Z", placedBy: "Suresh Kumar", status: "standing" },
      { id: "b-02", memberId: "m9", amount: 198000, placedAt: "2025-12-20T10:02:00.000Z", placedBy: "Suresh Kumar", status: "standing" },
      { id: "b-03", memberId: "m17", amount: 196000, placedAt: "2025-12-20T10:04:00.000Z", placedBy: "Suresh Kumar", status: "withdrawn" },
      { id: "b-04", memberId: "m2", amount: 192000, placedAt: "2025-12-20T10:07:00.000Z", placedBy: "Suresh Kumar", status: "standing" },
    ],
    result: {
      winnerMemberId: "m2",
      winningBid: 192000,
      discount: 8000,
      perMemberDividend: 800,
      dividendRemainder: 0,
      netInstallment: 19200,
      totalCollected: 192000,
      winnerPendingDues: 0,
      winnerPayout: 192000,
      closedAt: "2025-12-20T10:45:00.000Z",
      closedBy: "Suresh Kumar",
    },
  },
  {
    id: "au-g12c1",
    groupId: "g12",
    cycle: 1,
    scheduledDate: "2025-01-10T00:00:00.000Z",
    status: "closed",
    closedAt: "2025-01-10T10:20:00.000Z",
    closedBy: "Priya Sharma",
    bids: [
      { id: "b-41", memberId: "m24", amount: 19800, placedAt: "2025-01-10T10:00:00.000Z", placedBy: "Priya Sharma", status: "standing" },
      { id: "b-42", memberId: "m20", amount: 19600, placedAt: "2025-01-10T10:03:00.000Z", placedBy: "Priya Sharma", status: "standing" },
      { id: "b-43", memberId: "m38", amount: 19400, placedAt: "2025-01-10T10:06:00.000Z", placedBy: "Priya Sharma", status: "standing" },
      { id: "b-44", memberId: "m5", amount: 19200, placedAt: "2025-01-10T10:09:00.000Z", placedBy: "Priya Sharma", status: "standing" },
    ],
    result: {
      winnerMemberId: "m5",
      winningBid: 19200,
      discount: 800,
      perMemberDividend: 80,
      dividendRemainder: 0,
      netInstallment: 1920,
      totalCollected: 19200,
      winnerPendingDues: 0,
      winnerPayout: 19200,
      closedAt: "2025-01-10T10:20:00.000Z",
      closedBy: "Priya Sharma",
    },
  },
];